import { useState } from 'react';
import { Link } from 'react-router-dom';
import { ArrowLeftIcon } from '@heroicons/react/24/outline';
import { PageHeader } from '@/components/layout';
import { Badge, Card, CardHeader, EmptyState, LoadingSpinner } from '@/components/common';
import { useMyTargetHistory, useMyTargets } from '@/hooks/useTargets';
import type {
  AchievementPoint,
  IncentiveEntryRow,
  IncentiveProjection,
  IncentiveRuleView,
  MyTarget,
} from '@/services/targets';
import type { TargetMetric, TargetPeriodType } from '@/types';
import { formatCurrency } from '@/utils/format';
import { formatDate } from '@/utils';

/**
 * #343 -- the POSP's own targets and incentives tracker (`/portal/targets`).
 *
 * Rupee incentives only: XP from the POSP hub is a separate points system and is
 * never shown or converted here. There is no leaderboard and no view of any other
 * POSP. Incentives are RECORDED by the broker; the platform pays nothing.
 *
 * Four distinct states per section: loading, failed, nothing set, ok. A failed
 * fetch is never reported as "no targets".
 */

const PERIODS: { value: TargetPeriodType; label: string }[] = [
  { value: 'month', label: 'Month' },
  { value: 'quarter', label: 'Quarter' },
  { value: 'year', label: 'Year' },
];

const STATUS_VARIANT: Record<string, 'success' | 'warning' | 'error' | 'info' | 'default'> = {
  accrued: 'warning',
  approved: 'info',
  paid: 'success',
  reversed: 'error',
};

function fmt(metric: TargetMetric, value: string | number): string {
  return metric === 'policy_count' ? String(Math.round(Number(value))) : formatCurrency(value);
}

function ruleText(rule: IncentiveRuleView): string {
  const what =
    rule.kind === 'lump_sum' ? formatCurrency(rule.amount ?? 0) : `${rule.percent}% of your commission`;
  return `At ${Number(rule.threshold_pct)}%: ${what}${rule.tier_label ? ` (${rule.tier_label})` : ''}`;
}

export function Sparkline({ points }: { points: AchievementPoint[] }) {
  const values = points.map((p) => Number(p.achieved));
  if (values.length < 2) return null;
  const max = Math.max(...values, 1);
  const step = 100 / (values.length - 1);
  const path = values.map((v, i) => `${(i * step).toFixed(1)},${(28 - (v / max) * 26).toFixed(1)}`).join(' ');
  return (
    <svg
      viewBox="0 0 100 30"
      className="h-8 w-28 text-primary-600 dark:text-primary-400"
      role="img"
      aria-label={`Trend over ${points.length} periods`}
    >
      <polyline points={path} fill="none" stroke="currentColor" strokeWidth="2" />
    </svg>
  );
}

function ProgressBar({ target }: { target: MyTarget }) {
  const pct = target.percentage ?? 0;
  const markers = target.projection.rules.map((r) => Number(r.threshold_pct));
  const scale = Math.max(100, ...markers);
  return (
    <div className="relative mt-2">
      <div
        role="progressbar"
        aria-label={`${target.name || 'Target'} achievement`}
        aria-valuenow={Math.round(pct)}
        aria-valuemin={0}
        aria-valuemax={100}
        className="h-2 bg-gray-200 dark:bg-gray-700 rounded-full overflow-hidden"
      >
        <div
          className="h-full bg-primary-600 rounded-full"
          style={{ width: `${Math.min((pct / scale) * 100, 100)}%` }}
        />
      </div>
      {markers.map((m, i) => (
        <span
          key={`${m}-${i}`}
          data-testid="milestone-marker"
          title={`${m}% of target`}
          className="absolute top-[-3px] h-3.5 w-0.5 bg-amber-500"
          style={{ left: `${(m / scale) * 100}%` }}
        />
      ))}
    </div>
  );
}

function Projection({ projection }: { projection: IncentiveProjection }) {
  // Hidden when the target carries no rules.
  if (projection.rules.length === 0) return null;
  return (
    <div className="mt-4 rounded-lg bg-gray-50 dark:bg-gray-900/50 p-4" data-testid="projection">
      <h4 className="text-sm font-semibold text-gray-900 dark:text-gray-100">Milestones</h4>
      <ul className="mt-1 space-y-1 text-sm text-gray-600 dark:text-gray-300">
        {projection.rules.map((r) => (
          <li key={r.id}>
            {r.reached ? '✓ ' : ''}
            {ruleText(r)}
          </li>
        ))}
      </ul>
      <h4 className="mt-3 text-sm font-semibold text-gray-900 dark:text-gray-100">
        If the period closed now
      </h4>
      <p className="text-lg font-semibold text-green-700 dark:text-green-400">
        {formatCurrency(projection.total)}
        {projection.total_pending ? ' + commission uplift pending' : ''}
      </p>
      <p className="text-xs text-gray-500 dark:text-gray-400">
        {projection.label}. {projection.lump_sum ? `Lump sum ${formatCurrency(projection.lump_sum.amount)}. ` : ''}
        {projection.uplift
          ? projection.uplift.pending
            ? 'Uplift is pending until your commission is calculated. '
            : `Uplift ${formatCurrency(projection.uplift.amount ?? 0)}. `
          : ''}
      </p>
      {projection.next_milestone && (
        <p className="mt-1 text-sm text-gray-700 dark:text-gray-200">
          Still needed for the {Number(projection.next_milestone.threshold_pct)}% milestone:{' '}
          <span className="font-medium">{projection.next_milestone.still_needed}</span>
        </p>
      )}
    </div>
  );
}

function TargetCard({ target }: { target: MyTarget }) {
  const filters = [target.insurer_name, target.insurance_type_name].filter(Boolean).join(' / ');
  return (
    <Card>
      <div className="flex flex-wrap items-start justify-between gap-2">
        <div>
          <h3 className="text-base font-semibold text-gray-900 dark:text-gray-100">
            {target.name || (target.metric === 'policy_count' ? 'Number of policies' : 'Premium')}
          </h3>
          <p className="text-xs text-gray-500 dark:text-gray-400">
            {formatDate(target.period_start)} to {formatDate(target.period_end)}
            {filters ? ` · ${filters}` : ''}
          </p>
        </div>
        <span className="text-sm text-gray-600 dark:text-gray-300">
          {target.days_left} day{target.days_left === 1 ? '' : 's'} left
        </span>
      </div>
      <div className="mt-3 grid grid-cols-3 gap-4">
        <div>
          <p className="text-xs text-gray-500 dark:text-gray-400">Target</p>
          <p className="text-sm font-medium text-gray-900 dark:text-gray-100">
            {fmt(target.metric, target.target_amount)}
          </p>
        </div>
        <div>
          <p className="text-xs text-gray-500 dark:text-gray-400">Achieved</p>
          <p className="text-sm font-medium text-green-600 dark:text-green-400">
            {fmt(target.metric, target.achieved)}
          </p>
        </div>
        <div>
          <p className="text-xs text-gray-500 dark:text-gray-400">Progress</p>
          <p className="text-sm font-medium text-gray-900 dark:text-gray-100">
            {target.percentage != null ? `${target.percentage.toFixed(1)}%` : 'No target set'}
          </p>
        </div>
      </div>
      <ProgressBar target={target} />
      <Projection projection={target.projection} />
      <div className="mt-3 flex items-center gap-3 text-xs text-gray-500 dark:text-gray-400">
        <Sparkline points={target.trend} />
        <span>Recent periods</span>
      </div>
    </Card>
  );
}

function EntryLine({ entry }: { entry: IncentiveEntryRow }) {
  return (
    <li className="flex flex-wrap items-center gap-2 text-sm">
      <span className="font-medium">{formatCurrency(entry.amount)}</span>
      <Badge variant={STATUS_VARIANT[entry.status] ?? 'default'}>{entry.status}</Badge>
      {entry.tier_label && <span className="text-gray-500">{entry.tier_label}</span>}
      {entry.paid_on && (
        <span className="text-gray-500 dark:text-gray-400">
          paid {formatDate(entry.paid_on)}
          {entry.paid_ref ? `, ref ${entry.paid_ref}` : ''}
        </span>
      )}
      {entry.reason && <span className="text-gray-500 dark:text-gray-400">{entry.reason}</span>}
    </li>
  );
}

export function MyTargetsPage() {
  const [periodType, setPeriodType] = useState<TargetPeriodType>('month');
  const mine = useMyTargets(periodType);
  const history = useMyTargetHistory(periodType);

  return (
    <div className="space-y-6">
      <Link
        to="/portal"
        className="inline-flex items-center gap-1 text-sm text-gray-600 dark:text-gray-300 hover:underline"
      >
        <ArrowLeftIcon className="h-4 w-4" aria-hidden="true" /> Reports
      </Link>
      <PageHeader title="My targets" subtitle="Targets, incentives and past periods" />

      <div role="group" aria-label="Period" className="inline-flex rounded-lg border border-gray-300 dark:border-gray-600">
        {PERIODS.map((p) => (
          <button
            key={p.value}
            type="button"
            aria-pressed={periodType === p.value}
            onClick={() => setPeriodType(p.value)}
            className={
              periodType === p.value
                ? 'px-4 py-1.5 text-sm bg-primary-600 text-white first:rounded-l-lg last:rounded-r-lg'
                : 'px-4 py-1.5 text-sm text-gray-700 dark:text-gray-200 first:rounded-l-lg last:rounded-r-lg'
            }
          >
            {p.label}
          </button>
        ))}
      </div>

      <section aria-label="My targets" className="space-y-4">
        {mine.isLoading ? (
          <LoadingSpinner />
        ) : mine.isError ? (
          <p role="alert" className="text-sm text-red-600 dark:text-red-400">
            Your targets could not be loaded. Please try again shortly.
          </p>
        ) : !mine.data || mine.data.targets.length === 0 ? (
          <EmptyState
            title="No targets set for this period"
            description="Your broker has not set a target for this period yet."
          />
        ) : (
          <>
            {mine.data.targets.map((t) => (
              <TargetCard key={t.id} target={t} />
            ))}
            <p className="text-xs text-gray-500 dark:text-gray-400">{mine.data.note}</p>
          </>
        )}
      </section>

      <Card>
        <CardHeader title="History" subtitle="Closed periods and what was earned" />
        {history.isLoading ? (
          <LoadingSpinner />
        ) : history.isError ? (
          <p role="alert" className="text-sm text-red-600 dark:text-red-400">
            Your history could not be loaded. Please try again shortly.
          </p>
        ) : !history.data || history.data.length === 0 ? (
          <p className="text-sm text-gray-500 dark:text-gray-400">No closed periods yet.</p>
        ) : (
          <ul className="divide-y divide-gray-200 dark:divide-gray-700">
            {history.data.map((h) => (
              <li key={h.target} className="py-3">
                <div className="flex flex-wrap items-center justify-between gap-2 text-sm">
                  <span className="font-medium text-gray-900 dark:text-gray-100">
                    {h.name || formatDate(h.period_start)}
                  </span>
                  <span className="text-gray-600 dark:text-gray-300">
                    {fmt(h.metric, h.achieved)} of {fmt(h.metric, h.target_amount)}
                    {h.percentage != null ? ` (${h.percentage.toFixed(1)}%)` : ''}
                  </span>
                </div>
                {h.entries.length > 0 && (
                  <ul className="mt-1 space-y-1">
                    {h.entries.map((e) => (
                      <EntryLine key={e.id} entry={e} />
                    ))}
                  </ul>
                )}
              </li>
            ))}
          </ul>
        )}
      </Card>
    </div>
  );
}

export default MyTargetsPage;
